import {
	type AggregationsAggregate,
	type SearchHit,
	type SearchResponseBody,
	type SearchSuggest,
	type SearchTotalHits,
} from '@elastic/elasticsearch/lib/api/types';

import { type WithContext } from '~/interfaces/WithContext';
import { type Loggable } from '~/traits/Loggable';
import { type Wallable } from '~/traits/Wallable';

import {
	type BettingSplitsESResult,
	type BettingSplitsMarket,
	type BettingSplitsResult,
	type ComputedAttributes,
} from '../types';
import { extractBet } from '../utils/extractBet.ts';
import { extractMarket } from '../utils/extractMarket.ts';
import { extractSubevent } from '../utils/extractSubevent.ts';

export type OriginalAggregations = {
	subevents: SubeventsAggregation;
};

export type SubeventsAggregation = {
	buckets: SubeventsBucket[];
};

export type AggregationBucket = {
	key: number;
};

export type SubeventsBucket = AggregationBucket & {
	markets: { buckets: MarketsBucket[] };
};

export type MarketsBucket = AggregationBucket & {
	bets: { buckets: BetsBucket[] };
};

export type BetsBucket = AggregationBucket & {
	best_odds: {
		hits: {
			hits: SearchHit<BettingSplitsESResult>[];
		};
	};
};

export type BettingSplitsESResultBody = SearchResponseBody<
	BettingSplitsESResult,
	Record<string, AggregationsAggregate>
>;

export type AggregatedBettingSplitsResultBody = SearchResponseBody<
	BettingSplitsResult,
	Record<string, AggregationsAggregate>
>;

export async function bettingSplitsResponseFactory<
	TConfig extends Wallable & WithContext,
>(
	this: Loggable,
	config: TConfig,
	searchResponse: BettingSplitsESResultBody,
	from = 0,
	hitsPerPage = 10,
): Promise<AggregatedBettingSplitsResultBody> {
	const aggregations = searchResponse.aggregations as OriginalAggregations;

	const { subevents: subeventsGroup, ...baseAggregations } = aggregations;

	const subevents = new Map<number, BettingSplitsResult>();
	const originalHits = new Map<number, SearchHit<BettingSplitsESResult>>();

	await Promise.all(
		subeventsGroup.buckets
			.slice(from, from + hitsPerPage)
			.map(async ({ key: subeventId, markets }: SubeventsBucket) => {
				let subevent = subevents.get(subeventId);
				await Promise.all(
					markets.buckets.map(
						async ({ bets, key: marketId }: MarketsBucket) => {
							let market = subevent?.markets.find(
								(m: BettingSplitsMarket) => m.marketId === marketId,
							);
							await Promise.all(
								bets.buckets.map(async ({ best_odds }: BetsBucket) => {
									const hits = best_odds.hits.hits;

									for (const hit of hits) {
										const { _source: result } = hit;
										if (!result) {
											continue;
										}

										const { subeventId } = result;
										if (!subevent) {
											try {
												subevent = extractSubevent(result, config.user);
											} catch (error) {
												this.logger.error(
													`Failed to parse subevent fields: ${subeventId}`,
													error,
												);
												continue;
											}
											subevent = extractSubevent(result, config.user);
											subevent.markets = [];
											subevents.set(subeventId, subevent);
											originalHits.set(subeventId, hit);
										}

										if (!market) {
											try {
												market = extractMarket(result);
											} catch (error) {
												this.logger.error(
													`Failed to parse market fields: ${marketId}`,
													error,
												);
												continue;
											}
											subevent.betTypeId.push(market.betTypeId);
											market.bets = [];
										}

										let bet;
										try {
											bet = extractBet(result);
										} catch (error) {
											this.logger.error(
												`Failed to parse bet fields: ${result.betId}`,
												error,
											);
											continue;
										}

										const existingBetIndex = market.bets.findIndex(
											({ betId }: { betId: number }) => betId === bet.betId,
										);
										if (existingBetIndex !== -1) {
											market.bets[existingBetIndex] = bet;
										} else {
											market.bets.push(bet);
										}
									}
								}),
							);

							if (
								market &&
								// TODO: Remove this when we start handling draws
								market.bets.filter(bet => bet.betName !== 'Draw').length === 2
							) {
								subevent?.markets.push(market);
							}
						},
					),
				);
			}),
	);

	const hits = Array.from(subevents.values())
		.filter(({ markets }) => markets.length > 0)
		.map(subevent => {
			const originalHit = originalHits.get(subevent.subeventId);
			return {
				...(originalHit || {}),
				_index: originalHit?._index || '',
				_source: subevent,
			};
		});

	return {
		...searchResponse,
		aggregations: baseAggregations,
		hits: {
			...searchResponse.hits,
			total: {
				...(searchResponse.hits.total as SearchTotalHits),
				value: subeventsGroup.buckets.length,
			},
			hits: config.isWalled ? hits.slice(0, 1) : hits,
		},
		suggest: searchResponse.suggest as Record<
			string,
			SearchSuggest<BettingSplitsResult & ComputedAttributes>[]
		>,
	};
}
